import { useCallback } from 'react';
import { normalizeLevel, satisfies, type AccessLevel, type ModuleKey } from '@gty/common';

import { useAuth } from './useAuth';

export interface PermissionsApi {
  /** Effective level for a module. Unknown module or missing map -> NONE. */
  levelFor: (module: ModuleKey) => AccessLevel;
  /** Does the role meet `required` for `module`? */
  allows: (module: ModuleKey, required: AccessLevel) => boolean;
}

/**
 * Permission checks for the UI layer.
 *
 * These are ADDITIVE — they exist so we do not show a user doors they cannot
 * open. They are trivially bypassable from devtools and are never the control
 * that matters; every route they gate has a server-side `@RequirePermission`
 * on the data behind it. A restriction that exists only here is a bug.
 *
 * The comparison itself is `satisfies()` from `@gty/common`, the same function
 * the Nest guard calls, so the client can never drift from the server rule.
 */
export function usePermissions(): PermissionsApi {
  const { permissions } = useAuth();

  const levelFor = useCallback(
    // `noUncheckedIndexedAccess` makes this `AccessLevel | undefined`, and
    // `normalizeLevel` turns undefined — and anything corrupt — into NONE.
    (module: ModuleKey): AccessLevel => normalizeLevel(permissions[module]),
    [permissions],
  );

  const allows = useCallback(
    (module: ModuleKey, required: AccessLevel): boolean => satisfies(levelFor(module), required),
    [levelFor],
  );

  return { levelFor, allows };
}
